/* Sequoia Fitness | Website: https://www.sequoia.fitness | Author: Ali Hosseini | Copyright (c) 2026 sequoia.fitness. All rights reserved. */
/* Designed and developed by Pouria Shabani | https://linkedin.com/in/pouriashabani */

import { Info, Lightbulb, Play, Target, TriangleAlert } from 'lucide-react'
import SiteShell from '../pages/SiteShell.jsx'
import VideoPlayer from './VideoPlayer.jsx'
import styles from './LibraryDetail.module.css'
import './library.css'
import { APP_LIBRARY_URL, EXERCISES, libPath, posterSrc, similarOf, thumbSrc, videoSrc } from './data.js'

function NumberedList({ icon: Icon, title, items, tone }) {
  if (!items?.length) return null
  return (
    <section className={styles.panel + ' ' + styles[tone]}>
      <h2 className={styles.panelTitle}><Icon size={20} />{title}</h2>
      <ol className={styles.list}>
        {items.map((item, i) => (
          <li key={i} className={styles.listItem}>
            <span className={styles.listNum}>{(i + 1).toLocaleString('fa-IR')}</span>
            <p>{item}</p>
          </li>
        ))}
      </ol>
    </section>
  )
}

function Group({ label, items }) {
  return (
    <div className={styles.group}>
      <span className={styles.areasLabel}>{label}</span>
      <div className={styles.chips}>
        {items.length ? items.map((m) => <span key={m} className={styles.chipStatic}>{m}</span>) : <span className={styles.empty}>—</span>}
      </div>
    </div>
  )
}

export default function LibraryDetailPage({ id }) {
  const exercise = EXERCISES.find((e) => e.id === id)
  const guide = exercise.guide
  const primaryLabel = exercise.muscles?.[0] || ''
  const similar = similarOf(id)
  return (
    <SiteShell variant="library-detail">
      <div className="seq-lib-scope">
        <div className={styles.root + ' ' + styles.landing} dir="rtl" style={{ minHeight: 0, background: 'transparent' }}>
          <header className={styles.hero}>
            {exercise.kind?.length > 0 && (
              <div className={styles.kinds}>
                {exercise.kind.map((k) => <span key={k} className={styles.kind}>{k}</span>)}
              </div>
            )}
            <h1 className={styles.title}>{exercise.title}</h1>
            <div className={styles.facts}>
              {exercise.level?.[0] && <span className={styles.fact}><span className={styles.factKey}>سطح</span>{exercise.level[0]}</span>}
              {exercise.equipment?.[0] && <span className={styles.fact}><span className={styles.factKey}>تجهیزات</span>{exercise.equipment[0]}</span>}
              {primaryLabel && <span className={styles.fact}><span className={styles.factKey}>عضله</span>{primaryLabel}</span>}
            </div>
          </header>
          <div className={styles.stage} style={{ gridTemplateColumns: "minmax(0, 1fr)", maxWidth: 640, width: "100%", marginInline: "auto" }}>
            <div className={styles.video}>
              <VideoPlayer src={videoSrc(id)} poster={posterSrc(id)} />
            </div>
          </div>
          {exercise.description && (
            <section className={styles.panel}>
              <h2 className={styles.panelTitle}><Info size={20} />توضیحات تمرین</h2>
              <p className={styles.body}>{exercise.description}</p>
            </section>
          )}
          {(exercise.regionLabel || guide?.description || guide?.function) && (
            <section className={styles.panel}>
              <h2 className={styles.panelTitle}><Target size={20} />ناحیه درگیر</h2>
              {exercise.regionLabel && <Group label="ناحیه اصلی" items={[exercise.regionLabel]} />}
              {exercise.regionsSecondary?.length > 0 && <Group label="ناحیه مکمل" items={exercise.regionsSecondary} />}
              {exercise.musclesPrimary?.length > 0 && <Group label="عضلات اصلی" items={exercise.musclesPrimary} />}
              {exercise.musclesSecondary?.length > 0 && <Group label="عضلات مکمل" items={exercise.musclesSecondary} />}
              {guide?.description && <p className={styles.body}>{guide.description}</p>}
              {guide?.function && <p className={styles.body}>{guide.function}</p>}
            </section>
          )}
          <NumberedList icon={Lightbulb} title="نکات طلایی تمرین" items={guide?.tips} tone="good" />
          <NumberedList icon={TriangleAlert} title="اشتباهات رایج" items={guide?.commonMistakes} tone="warn" />
          <section className={styles.related}>
            <h2 className={styles.sectionTitle}>تمرین‌های دیگه</h2>
            <div className={styles.relatedGrid}>
              {similar.map((ex) => (
                <a key={ex.id} className={styles.mini + ' seq-lib-card'} href={libPath(ex.id)}>
                  <div className={styles.miniThumb}>
                    <img className={styles.miniPoster} src={thumbSrc(ex.id)} alt={ex.title} loading="lazy" decoding="async" width="480" height="480" />
                    <span className={styles.miniShade} />
                    <span className={styles.miniPlay}><Play size={14} fill="currentColor" /></span>
                  </div>
                  <span className={styles.miniName}>{ex.title}</span>
                </a>
              ))}
            </div>
          </section>
          <div className="seq-lib-more">
            <p>برای دیدن بقیه‌ی ویدیوها و جستجوی هوشمند، کتابخونه‌ی کامل رو توی اپلیکیشن باز کن.</p>
            <a href={APP_LIBRARY_URL} target="_blank" rel="noopener">
              مشاهده‌ی ویدیوهای بیشتر
            </a>
          </div>
        </div>
      </div>
    </SiteShell>
  )
}
